import type { AABB, Vec2 } from '../../geo/types';
import type { LinearRGB } from '../../core/color';
import { distanceBeyond, perimeterOf, radialArcOf, rampColour, rampWeight } from './farRingSeam';

// A point beyond the seam averages the seam over this many metres either side of the arc of its ray from
// the centre for every metre it lies out: a 45° cone, so a sector edge blurs as fast as it leaves the seam.
// On a sector edge the window lies half in each run.
const LATERAL_SPREAD = 1;

// The arc a land run spans along the bounds, and the colour it carries out to nearFields by `distance`.
export interface SeamColourRun {
  readonly from: number;
  readonly to: number;
  readonly albedo: LinearRGB;
  readonly distance: number;
}

export interface SeamSample {
  readonly colour: LinearRGB;
  // The share of the point's own run in the whole window it averages, water included.
  readonly share: number;
}

export interface SeamField {
  sample(p: Vec2, own: SeamColourRun | null): SeamSample;
}

// How far either way along the bounds a point this far out averages the seam: only where this reaches a
// run end do the colour and the ditch share change across the ring and not just outward.
export function windowHalfWidth(beyond: number): number {
  return LATERAL_SPREAD * beyond;
}

function overlapAt(lo: number, hi: number, run: SeamColourRun, shift: number): number {
  return Math.max(0, Math.min(hi, run.to + shift) - Math.max(lo, run.from + shift));
}

function overlap(lo: number, hi: number, run: SeamColourRun, perimeter: number): number {
  return (
    overlapAt(lo, hi, run, -perimeter) +
    overlapAt(lo, hi, run, 0) +
    overlapAt(lo, hi, run, perimeter)
  );
}

function arcGap(arc: number, run: SeamColourRun, perimeter: number): number {
  return Math.min(
    ...[-perimeter, 0, perimeter].map((shift) =>
      Math.max(run.from + shift - arc, arc - run.to - shift, 0)
    )
  );
}

// Each run adds its ramped albedo's difference from the base, weighed by the arc it covers in the
// window; null where the window holds no land. Every vertex of the far ring asks this, so it sums in
// place rather than build a colour per run.
function blend(
  runs: readonly SeamColourRun[],
  window: readonly [number, number, number],
  base: LinearRGB,
  beyond: number
): LinearRGB | null {
  const [lo, hi, perimeter] = window;
  let [total, red, green, blue] = [0, 0, 0, 0];
  for (const run of runs) {
    const weight = overlap(lo, hi, run, perimeter);
    if (weight === 0) continue;
    const kept = weight * (1 - rampWeight(run.distance, beyond));
    total += weight;
    red += kept * (run.albedo[0] - base[0]);
    green += kept * (run.albedo[1] - base[1]);
    blue += kept * (run.albedo[2] - base[2]);
  }
  if (total === 0) return null;
  return [base[0] + red / total, base[1] + green / total, base[2] + blue / total];
}

// The colour weighs the land runs in the window alone, so it does not pale towards the water beside a
// shore. The share counts the water against the run, or a run between two rivers would carry its ditches
// out for kilometres; it is half at most on a sector edge, where neither run may keep its ditches.
export function createSeamField(
  runs: readonly SeamColourRun[],
  bounds: AABB,
  base: LinearRGB
): SeamField {
  const perimeter = perimeterOf(bounds);
  const nearest = (arc: number) =>
    runs.reduce((best, run) =>
      arcGap(arc, run, perimeter) < arcGap(arc, best, perimeter) ? run : best
    );
  return {
    sample(p, own) {
      const beyond = distanceBeyond(p, bounds);
      if (beyond === 0 && own) return { colour: own.albedo, share: 1 };
      const arc = radialArcOf(p, bounds);
      const half = Math.min(windowHalfWidth(beyond), perimeter / 2);
      const share =
        own && half > 0 ? overlap(arc - half, arc + half, own, perimeter) / (2 * half) : 0;
      const colour = blend(runs, [arc - half, arc + half, perimeter], base, beyond);
      if (colour) return { colour, share };
      const run = nearest(arc);
      return { colour: rampColour(run.albedo, base, run.distance, beyond), share };
    },
  };
}

// Ditches keep full contrast while their run fills the window and are gone where it fills half, so two
// ditch patterns never meet along a sector edge.
export function ditchContrast(share: number): number {
  return Math.min(1, Math.max(0, 2 * share - 1));
}
